import React, { useState, useEffect, useRef } from 'react';
import { SVG } from '@svgdotjs/svg.js';

const Vigas = () => {
  const canvasRef = useRef(null);
  const [b, setB] = useState(0.25); // Ancho de la viga en metros
  const [h, setH] = useState(0.35); // Altura de la viga en metros
  const [selectedLayer, setSelectedLayer] = useState('inferior');
  const [zoom, setZoom] = useState(1);
  const [padding, setPadding] = useState(20); 
  const [Mu, setMu] = useState(5); 
  const [results, setResults] = useState([]);
  const [column, setColumn] = useState(0.3);
  const [selectedRow, setSelectedRow] = useState(null);
  const stirrupDiamenter = 10; //Diámetro en milímetros

  const canvasSize = 1000;

  // Capas de refuerzo
  const [layers, setLayers] = useState({
    inferior: { numBars: 4, barDiameter: 16 },
    superior: { numBars: 2, barDiameter: 12 },
    intermedio: { numBars: 0, barDiameter: 0 }, // Se calcula automáticamente
  });

  const barOptions = [8, 10, 12, 14, 16, 18, 20, 22, 25, 28, 32, 36]; // Diámetros en mm

  /*
  const rebar = 
  [
    {
      d : 8, //en mm
      pl : 0.395 //en kg/m
    },
    {
      d : 10, //en mm
      pl : 0.617 //en kg/m
    },
    {
      d : 12, //en mm
      pl : 0.888 //en kg/m
    },
    {
      d : 14, //en mm
      pl : 1.208 //en kg/m
    },
    {
      d : 16, //en mm
      pl : 1.578 //en kg/m
    },
    {
      d : 18, //en mm
      pl : 1998 //en kg/m
    },
    {
      d : 20, //en mm
      pl : 2.466 //en kg/m
    },
  ]
  */

  // Función para calcular el área de refuerzo
  const calculateSteelArea = (numBars, diameter) => 
  {
    const radius = diameter / 20; // Convertir mm a cm y obtener radio
    const areaOneBar = Math.PI * Math.pow(radius, 2); // Área de una barra
    return (areaOneBar * numBars).toFixed(2); // Área total en cm²
  };

  // Función de zoom
  const handleZoom = (event) => {
    event.preventDefault();
    const delta = event.deltaY > 0 ? 0.9 : 1.1;
    setZoom((prevZoom) => Math.min(Math.max(prevZoom * delta, 0.5), 3));
  };

  // Cálculo de refuerzo intermedio
  useEffect(() => {
    const maxDist = 0.6; // Distanciamiento máximo en metros
    setLayers((prevLayers) => ({
      ...prevLayers,
      intermedio: h > maxDist
        ? { numBars: 2, barDiameter: 12 }
        : { numBars: 0, barDiameter: 0 }
    }));
  }, [h]);

  useEffect(() => 
  {
    canvasRef.current.innerHTML = '';

    const draw = SVG().addTo(canvasRef.current).size('100%', '100%').viewbox(0, 0, canvasSize, canvasSize);
    const scale = canvasSize * zoom;

    const beamWidth = b * scale  ;
    const beamHeight = h * scale;
    const centerX = (canvasSize - beamWidth) / 2;
    const centerY = (canvasSize - beamHeight) / 2;

    // Dibujar la viga
    draw
      .rect(beamWidth, beamHeight)
      .fill('#e2e8f0')
      .stroke({ color: '#2d3748', width: 2 })
      .move(centerX, centerY);

    // Función para dibujar barras de refuerzo
    const drawMediumBars = (layer) => 
    {
      const { numBars, barDiameter } = layers[layer];
      const effectiveWidth = beamWidth - 2 * padding - 2 * stirrupDiamenter - barDiameter; // Consideramos el recubrimiento
      const barSpacing = Number(numBars > 1 ? effectiveWidth / (numBars - 1) : 0).toFixed(2); // Evitar división por 0 si hay solo 1 varilla

      for (let i = 0; i < numBars; i++) 
      {
        draw
          .circle(barDiameter)
          .fill(selectedLayer === layer ? '#dc2626' : '#2d3748')
          .move(
            (canvasSize/2 + padding + stirrupDiamenter + i * barSpacing) - (beamWidth/2),
            (canvasSize/2 + beamHeight/2 - barDiameter/2) - (beamHeight/2)
          )
          .click(() => setSelectedLayer(layer));
      }
    };

    const drawSuperiorBars = (layer) => 
    {
      const { numBars, barDiameter } = layers[layer];
      const effectiveWidth = beamWidth - 2 * padding - 2 * stirrupDiamenter - barDiameter; // Consideramos el recubrimiento
      const barSpacing = Number(numBars > 1 ? effectiveWidth / (numBars - 1) : 0).toFixed(2); // Evitar división por 0 si hay solo 1 varilla  

      for (let i = 0; i < numBars; i++) 
      {
        draw
          .circle(barDiameter)
          .fill(selectedLayer === layer ? '#dc2626' : '#2d3748')
          .move(
            (canvasSize/2 + padding + stirrupDiamenter + i * barSpacing) - (beamWidth/2),
            (canvasSize/2 + padding + stirrupDiamenter) - (beamHeight/2)
          )
          .click(() => setSelectedLayer(layer));
      }
    };  

    const drawInferiorBars = (layer) => 
    {
      const { numBars, barDiameter } = layers[layer];
      const effectiveWidth = beamWidth - 2 * padding - 2 * stirrupDiamenter - barDiameter; // Consideramos el recubrimiento
      const barSpacing = Number(numBars > 1 ? effectiveWidth / (numBars - 1) : 0).toFixed(2); // Evitar división por 0 si hay solo 1 varilla

      for (let i = 0; i < numBars; i++) 
      {
        draw
          .circle(barDiameter)
          .fill(selectedLayer === layer ? '#dc2626' : '#2d3748')
          .move(
            (canvasSize/2 + padding + stirrupDiamenter + i * barSpacing) - (beamWidth/2),
            (canvasSize/2 + beamHeight - padding - stirrupDiamenter - barDiameter) - (beamHeight/2)
          )
          .click(() => setSelectedLayer(layer));
      }

      //const d = Number(h - padding / 1000 - (barDiameter / 2) / 1000).toFixed(4);
    };

    const drawStirrups = () => 
    {
      draw
        .rect(
          beamWidth - 2 * padding - stirrupDiamenter, 
          beamHeight - 2 * padding - stirrupDiamenter
        )
        .fill('none')
        .stroke({ color: '#4a5568', width: (10 / 1000) * scale }) // Grosor de 10 mm escalado
        .radius((10 / 1000) * scale) // Radio de las esquinas
        .move(
          (canvasSize/2 + padding + stirrupDiamenter/2) - (beamWidth/2), 
          (canvasSize/2 + padding + stirrupDiamenter/2) - (beamHeight/2)   
        );      
    };

    console.log(22 * 0.617 * ((beamWidth - 2 * padding - stirrupDiamenter) * 2 + (beamHeight - 2 * padding - stirrupDiamenter) * 2 + 100 * 2)/1000)

    drawSuperiorBars('superior');
    drawInferiorBars('inferior');    

    drawStirrups();

    if (layers.intermedio.numBars > 0) 
    {
      drawMediumBars('intermedio', centerY + beamHeight / 2);
    }

  }, [b, h, layers, selectedLayer, zoom, padding]);

  const calculateBeamSectionUltimateMoment = ( b, d, AsInput ) => 
  {
    //b en metros
    //d en metros 
    //As en cm²
    const beta1 = 0.85;   // Para concretos comunes
    const phi = 0.9;      // Factor de reducción de resistencia
    const fc = 210;       // Resistencia del concreto (kg/cm²)
    const fy = 4200;      // Esfuerzo de fluencia del acero (kg/cm²)
  
    // Convertir de metros a centímetros
    const bCm = b * 100;
    const dCm = d * 100;  
  
    const As = AsInput; // 6.03 cm2 es el área de 3 varillas de diámetro 16 mm, hay q revisar bien el acero mínimo
  
    // 2. Calcular a y c para Mu
    const c = (As * fy) / (0.85 * fc * bCm);
    const a = beta1 * c;
  
    // 3. Calcular el Momento Último
    const Mu = phi * As * fy * (dCm - (a / 2)); // kg·cm
  
    // Convertir de kg·cm a kg·m
    //const MuKgM = Mu / 100;

    // Convertir de kg·cm a T·m
    const MuTM = (Mu / 100) / 1000;  
  
    return Number(MuTM.toFixed(2));     
  };

  /*
  const calculateAsmin = (b, d) => 
  {   
    // b y d en metros
    const fc = 210;  // Resistencia del concreto (kg/cm²)
    const fy = 4200; // Esfuerzo de fluencia del acero (kg/cm²)
  
    // Convertir de metros a centímetros
    const bCm = b * 100;
    const dCm = d * 100;    
  
    // Cálculo de As mínimo según ACI 318
    const AsMin1 = (3 * Math.sqrt(fc) * bCm * dCm) / fy;
    const AsMin2 = (20 * bCm * dCm) / fy;
 
    // Tomar el valor mayor
    const AsMin = Math.max(AsMin1, AsMin2);
   
    return Number(AsMin.toFixed(2)); // Retornar en cm²
  };
  */  

  const startLoop = (MuInput, column) => 
  {
    setSelectedRow(null);
    const diameters = [12, 14, 16, 18, 20, 22, 25, 28, 32, 36];
    let greaterThanMuCount = 0; // Contador de varillas mayores a MuInput
    const minSpacing01 = 2.5; // Espaciamiento mínimo en cm
    setResults([]);
    
    // Loop principal para `b` y `h`
    for (let b = 0.15; b <= column; b += 0.05) 
    {
      for (let h = 0.25; h <= b * 2.5; h += 0.05) 
      {
        for (let dIndex = 0; dIndex < diameters.length; dIndex++) 
        {
          const barDiameter = diameters[dIndex];
  
          // Ahora solo iteramos sobre el número de varillas con un setInterval
          let numBars = 2;
  
          const interval = setInterval(() => 
          {
            const areaSteel = calculateSteelArea(numBars, barDiameter); // Calcular el área de acero
    
            // Calcular `d` usando la fórmula proporcionada
            const d = Number(h - padding / 1000 - (barDiameter / 2) / 1000).toFixed(4);
    
            // Calcular el momento último usando los valores
            const MuSection = calculateBeamSectionUltimateMoment(b, d, areaSteel);  
    
            // Verificar espaciamiento entre varillas
            const minSpacing = Math.max(barDiameter/1000, minSpacing01/100);
            const calulatedSpacing = Number((b - 2 * padding/1000 - numBars * barDiameter/1000)/(numBars - 1)).toFixed(4);            

            if (calulatedSpacing <= minSpacing) 
            {              
              clearInterval(interval); // Detener el setInterval si no cumple el espaciamiento
              return;
            }

            // Compara MuSection con MuInput
            if (MuSection > MuInput) 
            {
              greaterThanMuCount++; // Si el valor calculado es mayor a Mu
            } 
            else 
            {
              greaterThanMuCount = 0; // Si no lo es, reiniciar el contador
            }

            // Si se detecta 2 veces seguidas que el valor calculado es mayor a Mu, salimos del loop de número de varillas
            if (greaterThanMuCount >= 2) 
            {
              //clearInterval(interval); // Salir del loop y detener el setInterval
              //return; // Salir de la función
            }
    
            // Solo mostrar valores que estén entre Mu y 1.10 * Mu (5% de Mu)
            if (MuSection > MuInput && MuSection <= 1.05 * MuInput) 
            {
              setB(Number(b).toFixed(2));
              setH(Number(h).toFixed(2));
              setLayers(prevLayers => (
              {
                ...prevLayers,
                inferior: { numBars: numBars-1, barDiameter }
              }));              
  
              setResults(prevResults => [
                ...prevResults,
                {
                  b: Number(b).toFixed(2),
                  h: Number(h).toFixed(2),
                  volumen:Number(b * h * 1).toFixed(4),
                  numBars: numBars,
                  barDiameter,
                  MuSection,
                }
              ]);
            }
  
            // Incrementar el número de varillas
            numBars++;
  
            // Si ya hemos recorrido todas las varillas posibles (de 2 a 5), salimos del setInterval
            if (numBars > 5) 
            {
              clearInterval(interval);
            }
          }, 1); // El retardo entre cada iteración es de 1000ms
        }
      }
    }
  };  

  const handleRowClick = (index, result) => {
    setSelectedRow(index);
  
    // Actualizar los valores según la fila seleccionada
    setB(result.b);
    setH(result.h);
    setLayers(prevLayers => ({
      ...prevLayers,
      inferior: {
        numBars: result.numBars,
        barDiameter: result.barDiameter
      }
    }));
  };

  // Cálculo del espaciamiento mínimo
  const isMinSpacing = (numBars, barDiameter) => 
  {
    const minSpacing01 = 2.5; // Espaciamiento mínimo en cm
    const minSpacing = Math.max(barDiameter/1000, minSpacing01/100);
    const calulatedSpacing = Number((b - 2 * padding/1000 - numBars * barDiameter/1000)/(numBars - 1)).toFixed(4);            
    
    if (calulatedSpacing <= minSpacing) 
    {
      return false;
    }
    else
    {
      return true;
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      {/* Canvas para el dibujo */}
      <div
        className="w-3/6 flex items-center justify-center"
        onWheel={handleZoom}
      >
        <div
          ref={canvasRef}
          className="bg-white shadow w-full h-[100vh] overflow-hidden"
        ></div>
      </div>

      {/* Menú de configuración */}
      <div className="w-1/6 max-h-1/2 p-4 bg-white shadow rounded-lg overflow-y-scroll overflow-visible">
        <h2 className="text-xl font-bold mb-4">Configuración</h2>
        <div className="mb-4">
          <label className="block text-gray-700">Ancho de la Viga (b) [m]:</label>
          <input
            type="number"
            step="0.05"
            min="0.15"
            max="1"
            value={b}
            onChange={(e) => 
              {
                const value = Number(e.target.value);
                const min = 0.15;
                const max = 1.00;
          
                if (isNaN(value) || value < min || value > max) {
                  alert(`Ingrese un valor entre ${min} y ${max} metros`);
                  return;
                }
          
                setB(value);
              }}
              
            className="w-full border rounded p-2"
          />
        </div>
        <div className="mb-4">
          <label className="block text-gray-700">Altura de la Viga (h) [m]:</label>
          <input
            type="number"
            step="0.05"
            min="0.15"
            max="1.5"
            value={h}
            onChange={(e) => 
              {
                const value = Number(e.target.value);
                const min = 0.15;
                const max = 1.00;
          
                if (isNaN(value) || value < min || value > max) {
                  alert(`Ingrese un valor entre ${min} y ${max} metros`);
                  return;
                }
          
                setH(value);
              }}
            className="w-full border rounded p-2"
          />
        </div>

        <div className="mb-4">
          <label className="block text-gray-700">Recubrimiento [cm]:</label>
          <input
            type="number"
            step="1"
            min="2"
            max="10"
            value={padding/10}
            onChange={(e) => 
              {
                const value = Number(e.target.value);
                const min = 2;
                const max = 10;
          
                if (isNaN(value) || value < min || value > max) {
                  alert(`Ingrese un valor entre ${min} y ${max} cm`);
                  return;
                }
          
                setPadding(parseFloat(value * 10))

                setLayers((prev) => ({
                  ...prev,
                  [selectedLayer]: { ...prev[selectedLayer], numBars: parseInt(2) }
                }))
              }}
            className="w-full border rounded p-2"
          />
        </div>

        <h3 className="text-lg font-semibold mb-2">Capa Seleccionada: {selectedLayer.charAt(0).toUpperCase() + selectedLayer.slice(1)}</h3>
        <div className="mb-4">
          <label className="block text-gray-700">Número de Varillas:</label>
          <input
            type="number"
            min="2"
            max="10"
            value={layers[selectedLayer].numBars}
            onChange={(e) => 
              {
                const value = Number(e.target.value);
                const min = 2;
                const max = 10;
          
                if (isNaN(value) || value < min || value > max) 
                {
                  alert(`Ingrese un valor entre ${min} y ${max} varillas`);
                  return;
                }

                if (!isMinSpacing(value, layers[selectedLayer].barDiameter)) 
                {
                  alert(`En un ancho de viga de ${b} metros\nsolo entran ${value - 1} varillas ∅ ${layers[selectedLayer].barDiameter}`);
                  return;
                }               
          
                setLayers((prev) => ({
                  ...prev,
                  [selectedLayer]: { ...prev[selectedLayer], numBars: parseInt(value) }
                }))
              }}
            className="w-full border rounded p-2"
          />
        </div>
        <div className="mb-4">
          <label className="block text-gray-700">Diámetro de Barras [mm]:</label>
          <select
            value={layers[selectedLayer].barDiameter}
            onChange={(e) => 
              {
                setLayers((prev) => ({
                  ...prev,
                  [selectedLayer]: { ...prev[selectedLayer], barDiameter: parseInt(e.target.value) }
                }));

                setLayers((prev) => ({
                  ...prev,
                  [selectedLayer]: { ...prev[selectedLayer], numBars: parseInt(2) }
                }))
              }
            }
            className="w-full border rounded p-2"
          >
            {barOptions.map((diameter) => (
              <option key={diameter} value={diameter}>
                {diameter} mm
              </option>
            ))}
          </select>
        </div>

        <h3 className="text-lg font-semibold mb-2">Áreas Acero de Refuerzo</h3>
        <div className="mt-4">
          <h3 className="text-lg font-semibold">Superior:</h3>
          <p className="text-xl">{calculateSteelArea(layers.superior.numBars, layers.superior.barDiameter)} cm²</p>
        </div>
        
        {calculateSteelArea(layers.intermedio.numBars, layers.intermedio.barDiameter) !== 0 &&
          <div className="mt-4">
            <h3 className="text-lg font-semibold">Intermedia:</h3>
            <p className="text-xl">{calculateSteelArea(layers.intermedio.numBars, layers.intermedio.barDiameter)} cm²</p>
          </div>
        }

        <div className="mt-4">
          <h3 className="text-lg font-semibold">Inferior:</h3>
          <p className="text-xl">{calculateSteelArea(layers.inferior.numBars, layers.inferior.barDiameter)} cm²</p>
        </div>
  
        <h3 className="text-lg font-semibold mb-2">Mu de la Sección</h3>
        <div className="mt-4">
          <p className="text-xl">{calculateBeamSectionUltimateMoment(b, Number(h - padding/1000 - (layers.inferior.barDiameter/2)/1000).toFixed(2), calculateSteelArea(layers.inferior.numBars, layers.inferior.barDiameter))} T-m</p>
        </div>
      </div>

      {/* Resultados secciones */}
      <div className="w-2/6 p-4 bg-white shadow rounded-lg overflow-y-scroll">
        <h2 className="text-xl font-bold mb-4">CARGA ELEMENTO</h2>
        <div className="mb-4">
          <label className="block text-gray-700">Mu [T - m]:</label>
          
          <input
            type="number"
            value={Mu}            
            onChange={(e) => 
              {
                const value = Number(e.target.value);
                const min = 1;
                const max = 100;
          
                if (isNaN(value) || value < min || value > max) 
                {
                  alert(`Ingrese un valor entre ${min} y ${max} T - m`);
                  return;
                }

                setMu(value);
              }}
            className="w-full border rounded p-2"
          />
        </div>

        <button 
          onClick={() => startLoop(Mu, column)} 
          className="px-4 py-2 bg-green-600 text-white font-semibold rounded-lg shadow-md hover:bg-green-700 transition duration-300 active:scale-95"
        >
          Calcular
        </button>

        <h2 className="text-xl font-bold mb-4">Secciones</h2>
        
        <div className="mt-4 max-h-[600px] overflow-y-auto border border-gray-200 rounded-lg">
          <table className="min-w-full border-collapse border border-gray-200">
            <thead>
              <tr>
                <th className="border p-2">b (m)</th>
                <th className="border p-2">h (m)</th>
                <th className="border p-2">Volumen (m³)</th>
                <th className="border p-2"># Varillas</th>
                <th className="border p-2">Diámetro (mm)</th>
                <th className="border p-2">Mu Sección</th>
              </tr>
            </thead>
            <tbody>
              {results.map((result, index) => (
                <tr
                  key={index}
                  className={`cursor-pointer ${
                    index === selectedRow ? 'bg-green-200' : 'hover:bg-gray-100'
                  }`}
                  onClick={() => handleRowClick(index, result)}
                >
                  <td className="border p-2">{result.b}</td>
                  <td className="border p-2">{result.h}</td>
                  <td className="border p-2">{result.volumen}</td>
                  <td className="border p-2">{result.numBars}</td>
                  <td className="border p-2">{result.barDiameter}</td>
                  <td className="border p-2">{result.MuSection.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>      
    </div>
  );
};

export default Vigas;